import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/swiper-react.mjs';
import { Mousewheel, Keyboard } from 'swiper/modules/index.mjs';
import 'swiper/swiper-bundle.css';
import { getTranslations, detectLanguage } from './i18n';
import { getConfig } from './config';

// Parcel 2에서 이미지를 로드하는 방법
const headerImageSrc = new URL('./assets/images/header.jpg', import.meta.url).href;
const headerImageWebp = new URL('./assets/images/header.webp', import.meta.url).href;

const gallery1Src = new URL('./assets/images/gallery-1.jpg', import.meta.url).href;
const gallery1Webp = new URL('./assets/images/gallery-1.webp', import.meta.url).href;
const gallery2Src = new URL('./assets/images/gallery-2.jpg', import.meta.url).href;
const gallery2Webp = new URL('./assets/images/gallery-2.webp', import.meta.url).href;
const gallery3Src = new URL('./assets/images/gallery-3.jpg', import.meta.url).href;
const gallery3Webp = new URL('./assets/images/gallery-3.webp', import.meta.url).href;
const gallery4Src = new URL('./assets/images/gallery-4.jpg', import.meta.url).href;
const gallery4Webp = new URL('./assets/images/gallery-4.webp', import.meta.url).href;
const gallery5Src = new URL('./assets/images/gallery-5.jpg', import.meta.url).href;
const gallery5Webp = new URL('./assets/images/gallery-5.webp', import.meta.url).href;
const gallery6Src = new URL('./assets/images/gallery-6.jpg', import.meta.url).href;
const gallery6Webp = new URL('./assets/images/gallery-6.webp', import.meta.url).href;
const gallery7Src = new URL('./assets/images/gallery-7.jpg', import.meta.url).href;
const gallery7Webp = new URL('./assets/images/gallery-7.webp', import.meta.url).href;
const gallery8Src = new URL('./assets/images/gallery-8.jpg', import.meta.url).href;
const gallery8Webp = new URL('./assets/images/gallery-8.webp', import.meta.url).href;
const gallery9Src = new URL('./assets/images/gallery-9.jpg', import.meta.url).href;
const gallery9Webp = new URL('./assets/images/gallery-9.webp', import.meta.url).href;
const gallery10Src = new URL('./assets/images/gallery-10.jpg', import.meta.url).href;
const gallery10Webp = new URL('./assets/images/gallery-10.webp', import.meta.url).href;
const gallery11Src = new URL('./assets/images/gallery-11.jpg', import.meta.url).href;
const gallery11Webp = new URL('./assets/images/gallery-11.webp', import.meta.url).href;
const gallery12Src = new URL('./assets/images/gallery-12.jpg', import.meta.url).href;
const gallery12Webp = new URL('./assets/images/gallery-12.webp', import.meta.url).href;

const dividerSrc = new URL('./assets/images/divider.png', import.meta.url).href;
const dividerWebp = new URL('./assets/images/divider.webp', import.meta.url).href;

// 지도 앱 아이콘
const naverMapIcon = new URL('./assets/images/navermap.webp', import.meta.url).href;
const kakaoMapIcon = new URL('./assets/images/kakaomap.webp', import.meta.url).href;
const googleMapIcon = new URL('./assets/images/googlemap.webp', import.meta.url).href;
const languageIcon = new URL('./assets/svgs/ico_global.svg', import.meta.url).href;
// const mapImage = '/images/map.jpg';
// const calendarMapImage = '/images/calendar-map.jpg';


function App() {
  // 런타임에 config 가져오기
  const config = useMemo(() => getConfig(), []);
  const translations = useMemo(() => getTranslations(config), [config]);

  const [language, setLanguage] = useState(() => detectLanguage());
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [selectedDate, setSelectedDate] = useState(() => config.calendarSelectedDate);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // 번역 함수
  const t = (key) => {
    return translations[language]?.[key] || translations['ko'][key] || key;
  };

  // 언어 변경
  const changeLanguage = (lang) => {
    setLanguage(lang);
    setLanguageMenuOpen(false);
  };

  // 언어 이름 매핑
  const languageNames = {
    ko: '한국어',
    ja: '日本語'
  };
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [messageForm, setMessageForm] = useState({
    name: '',
    password: '',
    message: ''
  });
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [submittingMessage, setSubmittingMessage] = useState(false);
  const [deletingMessage, setDeletingMessage] = useState(false);
  const [currentMessagePage, setCurrentMessagePage] = useState(1);
  const [messagesPerPage, setMessagesPerPage] = useState(5);
  const [viewMessageModalOpen, setViewMessageModalOpen] = useState(false);
  const [viewMessage, setViewMessage] = useState(null);
  const mapRef = React.useRef(null);
  const mapInstanceRef = React.useRef(null);
  const swiperRef = React.useRef(null);
  const mapEventHandlersRef = React.useRef(null);
  const giftNoticeMeasureRef = React.useRef(null);
  const [splitGiftAndNotice, setSplitGiftAndNotice] = useState(false);
  const messageListRef = React.useRef(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const prevActiveSectionRef = React.useRef(0);

  // iOS 기기 감지
  const isIOS = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }, []);

  // 인트로 이미지 상태 관리
  const [showIntro, setShowIntro] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageEnded, setImageEnded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [imageTimestamp] = useState(() => Date.now()); // 컴포넌트 마운트 시 한 번만 생성

  // 사진 공유 팝업 상태 (결혼식 시작 시간 이후에만 표시)
  const [showPhotoSharePopup, setShowPhotoSharePopup] = useState(() => {
    const now = new Date();
    const weddingStart = new Date(config.countdownDate + '+09:00');
    return now >= weddingStart;
  });
  const introImageUrl = isIOS
    ? "https://images.shka-eunchan.com/introanim_low.webp"
    : "https://images.shka-eunchan.com/introanim.webp";
  const introImageRef = React.useRef(null);

  // 갤러리 이미지 배열 (WebP 우선, fallback 포함)
  const galleryImages = [
    { webp: gallery1Webp || null, fallback: gallery1Src },
    { webp: gallery2Webp || null, fallback: gallery2Src },
    { webp: gallery3Webp || null, fallback: gallery3Src },
    { webp: gallery4Webp || null, fallback: gallery4Src },
    { webp: gallery5Webp || null, fallback: gallery5Src },
    { webp: gallery6Webp || null, fallback: gallery6Src },
    { webp: gallery7Webp || null, fallback: gallery7Src },
    { webp: gallery8Webp || null, fallback: gallery8Src },
    { webp: gallery9Webp || null, fallback: gallery9Src },
    { webp: gallery10Webp || null, fallback: gallery10Src },
    { webp: gallery11Webp || null, fallback: gallery11Src },
    { webp: gallery12Webp || null, fallback: gallery12Src },
  ];

  // 이미지 미리 로드 함수
  const preloadImages = useCallback(() => {
    const imagesToPreload = [
      // 헤더 이미지 (WebP 우선)
      headerImageWebp,
      headerImageSrc,
      // 갤러리 이미지들 (WebP 우선)
      ...galleryImages.map(img => img.webp).filter(Boolean),
      ...galleryImages.map(img => img.fallback),
    ];

    imagesToPreload.forEach((src) => {
      if (src) {
        const img = new Image();
        img.src = src;
      }
    });
  }, []);

  // 컴포넌트 마운트 시 이미지 미리 로드
  useEffect(() => {
    preloadImages();
  }, [preloadImages]);

  // 네이버 지도 초기화
  // 지도 섹션은 6번째 슬라이드 (헤더:0, 인용구:1, 부모님:2, 캘린더:3, 갤러리:4, 메시지:5, 지도:6)
  const MAP_SECTION_INDEX = 6;

  useEffect(() => {
    const initMap = () => {
      // 이미 초기화되어 있으면 resize만 수행
      if (mapInstanceRef.current) {
        if (typeof window.naver !== 'undefined' && window.naver.maps) {
          setTimeout(() => {
            if (mapInstanceRef.current) {
              window.naver.maps.Event.trigger(mapInstanceRef.current, 'resize');
            }
          }, 300);
        }
        return;
      }

      if (!mapRef.current) {
        return;
      }

      try {
        if (typeof window.naver === 'undefined' || !window.naver.maps) {
          return;
        }

        // 지도 컨테이너가 DOM에 있는지 확인
        if (!mapRef.current || !mapRef.current.parentElement) {
          return;
        }

        const latlng = new window.naver.maps.LatLng(config.mapLat, config.mapLng);
        const mapOptions = {
          center: latlng,
          zoom: 17,
          zoomControl: true,
          zoomControlOptions: {
            position: window.naver.maps.Position.TOP_RIGHT,
          },
        };

        const map = new window.naver.maps.Map(mapRef.current, mapOptions);

        map.setCenter(latlng);

        // 마커 추가
        new window.naver.maps.Marker({
          position: latlng,
          map: map,
          title: config.mapName,
        });

        mapInstanceRef.current = map;

        // 지도가 초기화된 후 resize 이벤트를 호출하여 지도가 제대로 렌더링되도록 함
        setTimeout(() => {
          if (mapInstanceRef.current) {
            window.naver.maps.Event.trigger(mapInstanceRef.current, 'resize');
          }
        }, 300);

        // 지도 영역에서 발생하는 이벤트를 감지하여 Swiper 스크롤 제어
        if (mapRef.current) {
          const mapElement = mapRef.current;

          // 기존 이벤트 리스너 제거
          if (mapEventHandlersRef.current) {
            const { handleMouseEnter, handleMouseLeave, handleTouchStart, handleTouchEnd } = mapEventHandlersRef.current;
            mapElement.removeEventListener('mouseenter', handleMouseEnter);
            mapElement.removeEventListener('mouseleave', handleMouseLeave);
            mapElement.removeEventListener('touchstart', handleTouchStart);
            mapElement.removeEventListener('touchend', handleTouchEnd);
          }

          // 지도 영역에 마우스가 들어올 때 Swiper 비활성화
          const handleMouseEnter = () => {
            if (swiperRef.current) {
              swiperRef.current.allowTouchMove = false;
              swiperRef.current.mousewheel?.disable();
            }
          };

          // 지도 영역에서 마우스가 나갈 때 Swiper 활성화
          const handleMouseLeave = () => {
            if (swiperRef.current && !viewerOpen && !messageModalOpen && !deleteModalOpen) {
              swiperRef.current.allowTouchMove = true;
              swiperRef.current.mousewheel?.enable();
            }
          };

          // 터치 이벤트 처리
          const handleTouchStart = () => {
            if (swiperRef.current) {
              swiperRef.current.allowTouchMove = false;
            }
          };

          const handleTouchEnd = () => {
            // 터치 종료 후 약간의 지연을 두고 Swiper 활성화
            setTimeout(() => {
              if (swiperRef.current && !viewerOpen && !messageModalOpen && !deleteModalOpen) {
                swiperRef.current.allowTouchMove = true;
              }
            }, 100);
          };

          mapElement.addEventListener('mouseenter', handleMouseEnter);
          mapElement.addEventListener('mouseleave', handleMouseLeave);
          mapElement.addEventListener('touchstart', handleTouchStart, { passive: true });
          mapElement.addEventListener('touchend', handleTouchEnd, { passive: true });

          // 이벤트 핸들러를 ref에 저장하여 cleanup에서 제거할 수 있도록 함
          mapEventHandlersRef.current = {
            handleMouseEnter,
            handleMouseLeave,
            handleTouchStart,
            handleTouchEnd
          };
        }
      } catch (error) {
        // 네이버 지도 초기화 에러 무시
      }
    };

    // 지도 섹션이 활성화되었을 때만 초기화 시도
    if (activeSection === MAP_SECTION_INDEX) {
      // 네이버 지도 API가 로드될 때까지 대기
      if (typeof window.naver !== 'undefined' && window.naver.maps) {
        // 약간의 지연을 두어 DOM이 완전히 렌더링된 후 초기화
        setTimeout(initMap, 300);
      } else {
        let checkCount = 0;
        const maxChecks = 100; // 최대 10초 (100ms * 100)

        const checkNaver = setInterval(() => {
          checkCount++;
          if (typeof window.naver !== 'undefined' && window.naver.maps) {
            clearInterval(checkNaver);
            setTimeout(initMap, 300);
          } else if (checkCount >= maxChecks) {
            clearInterval(checkNaver);
          }
        }, 100);

        return () => {
          clearInterval(checkNaver);
          // 이벤트 리스너 정리
          if (mapRef.current && mapEventHandlersRef.current) {
            const { handleMouseEnter, handleMouseLeave, handleTouchStart, handleTouchEnd } = mapEventHandlersRef.current;
            mapRef.current.removeEventListener('mouseenter', handleMouseEnter);
            mapRef.current.removeEventListener('mouseleave', handleMouseLeave);
            mapRef.current.removeEventListener('touchstart', handleTouchStart);
            mapRef.current.removeEventListener('touchend', handleTouchEnd);
            mapEventHandlersRef.current = null;
          }
        };
      }
    }

    // cleanup 함수: 이벤트 리스너 제거
    return () => {
      if (mapRef.current && mapEventHandlersRef.current) {
        const { handleMouseEnter, handleMouseLeave, handleTouchStart, handleTouchEnd } = mapEventHandlersRef.current;
        mapRef.current.removeEventListener('mouseenter', handleMouseEnter);
        mapRef.current.removeEventListener('mouseleave', handleMouseLeave);
        mapRef.current.removeEventListener('touchstart', handleTouchStart);
        mapRef.current.removeEventListener('touchend', handleTouchEnd);
        mapEventHandlersRef.current = null;
      }
    };
  }, [activeSection, viewerOpen, messageModalOpen, deleteModalOpen]);

  // 마음 전하는 곳 + 안내사항 결합 섹션 높이 측정 (모바일 브라우저 UI 숨김 대응)
  useEffect(() => {
    let isMeasured = false;
    let scrollHandler = null;
    let visualViewportHandler = null;
    let scrollTimeoutId = null;

    const measureHeight = () => {
      if (!giftNoticeMeasureRef.current || isMeasured) return;

      // Visual Viewport API 사용 (지원되는 경우), fallback으로 window.innerHeight
      const availableHeight = window.visualViewport?.height || window.innerHeight;
      const contentHeight = giftNoticeMeasureRef.current.getBoundingClientRect().height;
      setSplitGiftAndNotice(contentHeight > availableHeight - 32);

      isMeasured = true;

      // 측정 완료 후 이벤트 리스너 제거
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler, { passive: true });
      }
      if (visualViewportHandler && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', visualViewportHandler);
      }
      if (scrollTimeoutId) {
        clearTimeout(scrollTimeoutId);
      }
    };

    // 초기 측정
    measureHeight();

    // Visual Viewport API가 지원되는 경우 사용
    if (window.visualViewport) {
      visualViewportHandler = () => {
        // 약간의 지연을 두어 브라우저 UI 숨김 완료 대기
        setTimeout(measureHeight, 100);
      };
      window.visualViewport.addEventListener('resize', visualViewportHandler);
    }

    // 스크롤 이벤트로 브라우저 UI 숨김 감지 (fallback)
    scrollHandler = () => {
      // 이전 타이머가 있으면 취소
      if (scrollTimeoutId) {
        clearTimeout(scrollTimeoutId);
      }
      // 스크롤이 끝난 후 1초 뒤에 측정
      scrollTimeoutId = setTimeout(measureHeight, 1000);
    };
    window.addEventListener('scroll', scrollHandler, { passive: true });

    // 최대 1초 후 강제 측정 (브라우저 UI가 숨겨지지 않는 경우 대비)
    const timeoutId = setTimeout(() => {
      measureHeight();
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
      if (scrollTimeoutId) {
        clearTimeout(scrollTimeoutId);
      }
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler, { passive: true });
      }
      if (visualViewportHandler && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', visualViewportHandler);
      }
    };
  }, [language]);

  // 키보드 이벤트 핸들러
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!viewerOpen) return;

      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'Escape') {
        setViewerOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewerOpen, currentImageIndex]);

  // 이전 이미지
  const handlePrevious = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1));
  };

  // 다음 이미지
  const handleNext = () => {
    setCurrentImageIndex((prev) => (prev === galleryImages.length - 1 ? 0 : prev + 1));
  };

  // 이미지 클릭 핸들러
  const handleImageClick = (index) => {
    setCurrentImageIndex(index);
    setViewerOpen(true);
  };

  // 지도 앱 열기 핸들러 (앱이 있으면 앱으로, 없으면 웹으로)
  const openMapApp = (appScheme, webUrl) => {
    const startTime = Date.now();
    let appOpened = false;

    // 앱이 열렸는지 확인하기 위한 플래그
    const checkAppOpened = () => {
      const elapsed = Date.now() - startTime;
      // 2초 이내에 페이지가 blur되면 앱이 열린 것으로 간주
      if (elapsed < 2000 && document.hidden) {
        appOpened = true;
      }
    };

    // 딥링크 시도
    try {
      window.location.href = appScheme;
    } catch (error) {
      // 딥링크 설정 실패 시 무시
    }

    // 페이지 blur 이벤트 감지
    const handleBlur = () => {
      appOpened = true;
      window.removeEventListener('blur', handleBlur);
    };
    window.addEventListener('blur', handleBlur);

    // 2초 후에도 앱이 열리지 않았으면 웹으로 fallback
    setTimeout(() => {
      if (!appOpened && !document.hidden) {
        window.open(webUrl, '_blank');
      }
      window.removeEventListener('blur', handleBlur);
    }, 2000);
  };

  // 네이버 지도 열기
  const handleNaverMap = () => {
    const lat = config.mapLat;
    const lng = config.mapLng;
    const name = config.mapName;
    const appScheme = `nmap://route?dlat=${lat}&dlng=${lng}&dname=${encodeURIComponent(name)}&appname=react-wedding-invite`;
    const webUrl = `https://map.naver.com/p/search/${encodeURIComponent(name)}`;
    openMapApp(appScheme, webUrl);
  };

  // 카카오 지도 열기
  const handleKakaoMap = () => {
    const lat = config.mapLat;
    const lng = config.mapLng;
    const name = config.mapName;
    const appScheme = `kakaomap://route?ep=${lat},${lng}&by=CAR`;
    const webUrl = `https://map.kakao.com/link/to/${encodeURIComponent(name)},${lat},${lng}`;
    openMapApp(appScheme, webUrl);
  };

  // 구글 지도 열기
  const handleGoogleMap = () => {
    const lat = config.mapLat;
    const lng = config.mapLng;
    const appScheme = `google.navigation:q=${lat},${lng}`;
    const webUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    openMapApp(appScheme, webUrl);
  };

  // 터치 시작 핸들러
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  // 터치 이동 핸들러
  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  // 터치 종료 핸들러
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50; // 최소 스와이프 거리

    if (distance > minSwipeDistance) {
      // 왼쪽으로 스와이프 (다음 이미지)
      handleNext();
    } else if (distance < -minSwipeDistance) {
      // 오른쪽으로 스와이프 (이전 이미지)
      handlePrevious();
    }
  };

  // 계좌번호 복사 핸들러
  const copyToClipboard = async (accountNumber, bankName) => {
    const textToCopy = `${bankName} ${accountNumber}`;
    try {
      await navigator.clipboard.writeText(textToCopy);
      alert(`${bankName} 계좌번호가 복사되었습니다.\n${textToCopy}`);
    } catch (err) {
      // 클립보드 API가 지원되지 않는 경우 fallback
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        alert(`${bankName} 계좌번호가 복사되었습니다.\n${textToCopy}`);
      } catch (fallbackErr) {
        alert(`계좌번호 복사에 실패했습니다. 직접 복사해주세요.\n${textToCopy}`);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleCopyGroomAccount = () => {
    copyToClipboard('3333-07-7881334', '카카오뱅크');
  };

  const handleCopyBrideAccount = () => {
    copyToClipboard('3333-08-2003559', '카카오뱅크');
  };

  const handleCopyReceptionAccount = () => {
    copyToClipboard('0604813003041', '신협 가재업');
  };

  // 메시지 불러오기
  const fetchMessages = async () => {
    try {
      setLoadingMessages(true);
      const response = await fetch('/api/messages');
      const data = await response.json();

      if (response.ok && data.messages) {
        // 시간 포맷 변환 (한국 시간 기준)
        const formattedMessages = data.messages.map(msg => {
          let timeStr = msg.createdAt || msg.time;

          // createdAt이 없고 timestamp만 있는 경우, 한국 시간으로 변환
          if (!timeStr && msg.timestamp) {
            const date = new Date(msg.timestamp);
            timeStr = date.toLocaleString('ko-KR', {
              year: '2-digit',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
              timeZone: 'Asia/Seoul'
            }).replace(/\. /g, '.').replace(/\./g, '.').replace(/, /g, ' | ').replace(/,/g, ' | ');
          }

          return {
            ...msg,
            time: timeStr || msg.timestamp
          };
        });
        setMessages(formattedMessages);
      }
    } catch (error) {
      // 메시지 불러오기 오류 무시
    } finally {
      setLoadingMessages(false);
    }
  };

  // 메시지 저장
  const handleSubmitMessage = async () => {
    if (!messageForm.name.trim() || !messageForm.password.trim() || !messageForm.message.trim()) {
      alert('이름, 비밀번호, 메시지를 모두 입력해주세요.');
      return;
    }

    try {
      setSubmittingMessage(true);
      const response = await fetch('/api/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(messageForm),
      });

      // 응답이 비어있는지 확인
      const responseText = await response.text();
      if (!responseText) {
        throw new Error('서버로부터 응답을 받지 못했습니다.');
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error('서버 응답을 파싱할 수 없습니다.');
      }

      if (response.ok && data.success) {
        alert('메시지가 성공적으로 등록되었습니다!');
        setMessageModalOpen(false);
        setMessageForm({ name: '', password: '', message: '' });
        // 메시지 목록 새로고침
        await fetchMessages();
      } else {
        alert(data.error || '메시지 등록에 실패했습니다.');
      }
    } catch (error) {
      alert(`메시지 등록 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setSubmittingMessage(false);
    }
  };

  // 메시지 삭제
  const handleDeleteMessage = async () => {
    if (!selectedMessageId || !deletePassword.trim()) {
      alert('비밀번호를 입력해주세요.');
      return;
    }

    try {
      setDeletingMessage(true);
      const response = await fetch('/api/messages', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messageId: selectedMessageId,
          password: deletePassword,
        }),
      });

      // 응답이 비어있는지 확인
      const responseText = await response.text();
      if (!responseText) {
        throw new Error('서버로부터 응답을 받지 못했습니다.');
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error('서버 응답을 파싱할 수 없습니다.');
      }

      if (response.ok && data.success) {
        alert('메시지가 삭제되었습니다.');
        setDeleteModalOpen(false);
        setSelectedMessageId(null);
        setDeletePassword('');
        // 메시지 목록 새로고침
        await fetchMessages();
      } else {
        alert(data.error || '메시지 삭제에 실패했습니다.');
      }
    } catch (error) {
      alert(`메시지 삭제 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setDeletingMessage(false);
    }
  };

  const handleOpenViewMessage = (msg) => {
    setViewMessage(msg);
    setViewMessageModalOpen(true);
  };

  const handleCloseViewMessage = () => {
    setViewMessage(null);
    setViewMessageModalOpen(false);
  };

  // 메시지 삭제 모달 열기
  const handleOpenDeleteModal = (messageId) => {
    setSelectedMessageId(messageId);
    setDeletePassword('');
    setDeleteModalOpen(true);
  };

  // 이미지 로드 완료 핸들러 (애니메이션이 완전히 로드되었는지 확인)
  const handleImageLoaded = () => {
    if (!imageLoaded && introImageRef.current) {
      const img = introImageRef.current;

      // 이미지가 완전히 로드되었는지 확인
      const checkImageFullyLoaded = () => {
        // 1. complete 속성 확인 (이미지가 완전히 로드되었는지)
        // 2. naturalWidth와 naturalHeight 확인 (이미지 메타데이터가 로드되었는지)
        if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
          // 추가로 이미지 decode() 메서드가 지원되는 경우 사용
          if (img.decode) {
            img.decode()
              .then(() => {
                // 이미지가 완전히 디코딩되었을 때 로드 완료 처리
                // WebP 애니메이션의 경우 약간의 추가 대기 시간을 두어 모든 프레임이 준비되도록 함
                setTimeout(() => {
                  if (!imageLoaded) {
                    setImageLoaded(true);
                  }
                }, 200); // 200ms 추가 대기
              })
              .catch(() => {
                // decode() 실패 시에도 complete와 naturalWidth/Height로 확인
                if (!imageLoaded) {
                  setImageLoaded(true);
                }
              });
          } else {
            // decode() 메서드가 지원되지 않는 경우
            // WebP 애니메이션의 경우 약간의 추가 대기 시간을 두어 모든 프레임이 준비되도록 함
            setTimeout(() => {
              if (!imageLoaded) {
                setImageLoaded(true);
              }
            }, 300); // 300ms 추가 대기
          }
        } else {
          // 아직 완전히 로드되지 않은 경우 잠시 후 재시도
          setTimeout(checkImageFullyLoaded, 100);
        }
      };

      checkImageFullyLoaded();
    }
  };


  // 이미지 에러 핸들러
  const handleImageError = () => {
    // 에러 발생 시 로딩 화면을 숨기고 바로 본 페이지로 이동
    setImageLoaded(true);
    setIsLoading(false);
    setTimeout(() => {
      setImageEnded(true);
      setTimeout(() => {
        setShowIntro(false);
      }, 1000);
    }, 500);
  };

  // 이미지 표시 후 일정 시간 후 자동으로 본 페이지로 이동 (예: 5초)
  // 사진 공유 팝업이 표시 중이면 인트로 자동 전환을 일시정지
  useEffect(() => {
    if (!showIntro || imageEnded || !imageLoaded || showPhotoSharePopup) return;

    const timer = setTimeout(() => {
      // 5초 후 본 페이지로 이동
      setImageEnded(true);
      setTimeout(() => {
        setShowIntro(false);
      }, 1000);
    }, 5000); // 5초

    return () => clearTimeout(timer);
  }, [showIntro, imageEnded, imageLoaded, showPhotoSharePopup]);

  // 로딩이 끝난 후 화면 크기 계산 재수행
  useEffect(() => {
    if (!showIntro) {
      // 로딩이 끝나고 본 페이지가 표시된 후 약간의 지연을 두고 화면 크기 계산
      const timeoutId = setTimeout(() => {
        // 마음 전하는 곳 + 안내사항 섹션 높이 재측정
        if (giftNoticeMeasureRef.current) {
          const availableHeight = window.visualViewport?.height || window.innerHeight;
          const contentHeight = giftNoticeMeasureRef.current.getBoundingClientRect().height;
          setSplitGiftAndNotice(contentHeight > availableHeight - 32);
        }

        // 메시지 섹션 높이 재계산
        if (messageListRef.current) {
          const availableHeight = messageListRef.current.clientHeight || 0;
          const estimatedItemHeight = 110; // 메시지 카드 추정 높이
          const next = Math.max(1, Math.floor(availableHeight / estimatedItemHeight));
          setMessagesPerPage(next);
        }
      }, 500); // 페이드아웃 애니메이션 완료 후 측정

      return () => clearTimeout(timeoutId);
    }
  }, [showIntro]);

  // 이미지 로드 타임아웃 처리 (5초 후 강제 완료)
  useEffect(() => {
    if (!showIntro || imageLoaded) return;

    const timeout = setTimeout(() => {
      if (!imageLoaded) {
        // 5초 후 진행률과 관계없이 이미지 로드 완료 처리
        setImageLoaded(true);
      }
    }, 5000); // 5초 타임아웃

    return () => clearTimeout(timeout);
  }, [showIntro, imageLoaded]);

  // 이미지 로드 완료 후 로딩 화면 제거
  useEffect(() => {
    if (imageLoaded && isLoading) {
      // 로딩 화면 페이드아웃 후 제거
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [imageLoaded, isLoading]);


  // 모달이 열릴 때 body 스크롤 막기
  useEffect(() => {
    const isModalOpen = viewerOpen || messageModalOpen || deleteModalOpen;

    if (isModalOpen) {
      // 현재 스크롤 위치 저장
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        // 모달이 닫힐 때 스크롤 위치 복원
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [viewerOpen, messageModalOpen, deleteModalOpen]);

  // Swiper 제어: 모달 시 입력 차단
  useEffect(() => {
    const swiper = swiperRef.current;
    const shouldLock = viewerOpen || messageModalOpen || deleteModalOpen;
    if (!swiper) return;
    if (shouldLock) {
      swiper.allowTouchMove = false;
      swiper.mousewheel?.disable();
      swiper.keyboard?.disable();
    } else {
      swiper.allowTouchMove = true;
      swiper.mousewheel?.enable();
      swiper.keyboard?.enable();
    }
  }, [viewerOpen, messageModalOpen, deleteModalOpen]);

  // 컴포넌트 마운트 시 메시지 불러오기
  useEffect(() => {
    fetchMessages();
  }, []);

  // 메시지 변경 시 페이지 보정
  useEffect(() => {
    const pages = Math.max(1, Math.ceil(messages.length / messagesPerPage));
    if (currentMessagePage > pages) {
      setCurrentMessagePage(pages);
    }
  }, [messages.length, messagesPerPage]);

  // 메시지 섹션 높이에 따라 페이지 당 항목 수 동적 계산 (모바일 브라우저 UI 숨김 대응)
  useEffect(() => {
    let isMeasured = false;
    let scrollHandler = null;
    let visualViewportHandler = null;
    let scrollTimeoutId = null;

    const calcMessagesPerPage = () => {
      if (!messageListRef.current || isMeasured) return;

      // Visual Viewport API 사용 (지원되는 경우), fallback으로 window.innerHeight
      // 메시지 리스트의 실제 clientHeight 사용
      const availableHeight = messageListRef.current.clientHeight || 0;
      const estimatedItemHeight = 110; // 메시지 카드 추정 높이
      const next = Math.max(1, Math.floor(availableHeight / estimatedItemHeight));
      setMessagesPerPage(next);

      isMeasured = true;

      // 측정 완료 후 이벤트 리스너 제거
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler, { passive: true });
      }
      if (visualViewportHandler && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', visualViewportHandler);
      }
      if (scrollTimeoutId) {
        clearTimeout(scrollTimeoutId);
      }
    };

    // 초기 측정 (메시지 리스트가 렌더링된 후)
    const initialTimeout = setTimeout(() => {
      calcMessagesPerPage();
    }, 100);

    // Visual Viewport API가 지원되는 경우 사용
    if (window.visualViewport) {
      visualViewportHandler = () => {
        // 약간의 지연을 두어 브라우저 UI 숨김 완료 대기
        setTimeout(calcMessagesPerPage, 100);
      };
      window.visualViewport.addEventListener('resize', visualViewportHandler);
    }

    // 스크롤 이벤트로 브라우저 UI 숨김 감지 (fallback)
    scrollHandler = () => {
      // 이전 타이머가 있으면 취소
      if (scrollTimeoutId) {
        clearTimeout(scrollTimeoutId);
      }
      // 스크롤이 끝난 후 1초 뒤에 측정
      scrollTimeoutId = setTimeout(calcMessagesPerPage, 1000);
    };
    window.addEventListener('scroll', scrollHandler, { passive: true });

    // 최대 1초 후 강제 측정 (브라우저 UI가 숨겨지지 않는 경우 대비)
    const timeoutId = setTimeout(() => {
      calcMessagesPerPage();
    }, 1000);

    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(timeoutId);
      if (scrollTimeoutId) {
        clearTimeout(scrollTimeoutId);
      }
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler, { passive: true });
      }
      if (visualViewportHandler && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', visualViewportHandler);
      }
    };
  }, []);

  // 첫 섹션에서만 언어 버튼 표시
  const languageButtonOpacity = activeSection === 0 ? 1 : 0;

  // 카운트다운 계산
  useEffect(() => {
    // 한국 시간(Asia/Seoul, UTC+9) 기준으로 명시적으로 파싱
    const targetDateStr = config.countdownDate;
    const targetDate = new Date(targetDateStr.includes('T') && !targetDateStr.includes('+') && !targetDateStr.includes('Z')
      ? targetDateStr + '+09:00'  // 타임존 정보가 없으면 한국 시간으로 추가
      : targetDateStr
    ).getTime();

    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = targetDate - now;

      if (distance > 0) {
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        setCountdown({ days, hours, minutes, seconds });
      } else {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, []);

  // 캘린더 날짜 생성 (3월 2026)
  const calendarDays = [];
  for (let i = 1; i <= 31; i++) {
    calendarDays.push(i);
  }

  const totalMessagePages = Math.max(1, Math.ceil(messages.length / messagesPerPage));
  const startMessageIndex = (currentMessagePage - 1) * messagesPerPage;
  const paginatedMessages = messages.slice(startMessageIndex, startMessageIndex + messagesPerPage);
  const messagePageNumbers = Array.from({ length: totalMessagePages }, (_, i) => i + 1);

  const handlePrevMessagePage = () => {
    setCurrentMessagePage((prev) => Math.max(1, prev - 1));
  };

  const handleNextMessagePage = () => {
    setCurrentMessagePage((prev) => Math.min(totalMessagePages, prev + 1));
  };

  const GiftSectionContent = () => {
    // reception 모드일 때 다른 UI 표시
    if (config.isReception) {
      return (
        <div className="flex flex-col items-center gap-8 w-full max-w-[328px] md:max-w-[500px] lg:max-w-[600px] mx-auto">
          {/* 제목과 설명 */}
          <div className="flex flex-col items-center gap-4 text-center">
            <h2 className="text-xl font-kopub font-bold text-wedding-gray-dark">
              {t('bankAccount')}
            </h2>
            <p className="text-base font-kopub text-wedding-gray-medium">
              {t('bankAccountDesc')}
            </p>
          </div>

          {/* 버튼 */}
          <div className="flex flex-col gap-4 w-[240px]">
            <button
              onClick={handleCopyReceptionAccount}
              className="w-full py-4 px-4 bg-[#639B89] rounded-[50px] text-white text-base font-pretendard font-semibold tracking-[0.1em] text-center flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              계좌번호 복사
            </button>
          </div>
        </div>
      );
    }

    // 기본 모드 (기존 UI)
    return (
      <div className="flex flex-col items-center gap-8 w-full max-w-[328px] md:max-w-[500px] lg:max-w-[600px] mx-auto">
        {/* 제목과 설명 */}
        <div className="flex flex-col items-center gap-4 text-center">
          <h2 className="text-xl font-kopub font-bold text-wedding-gray-dark">
            {t('bankAccount')}
          </h2>
          <p className="text-base font-kopub text-wedding-gray-medium">
            {t('bankAccountDesc')}
          </p>
        </div>

        {/* 버튼들 */}
        <div className="flex flex-col gap-4 w-[240px]">
          <button
            onClick={handleCopyGroomAccount}
            className="w-full py-4 px-4 bg-[#EAF5FF] rounded-[50px] text-wedding-gray-dark text-base font-pretendard font-semibold tracking-[0.1em] text-center flex items-center justify-center hover:opacity-80 transition-opacity"
          >
            {t('groomAccount')}
          </button>
          <button
            onClick={handleCopyBrideAccount}
            className="w-full py-4 px-4 bg-[#FFECE5] rounded-[50px] text-wedding-gray-dark text-base font-pretendard font-semibold tracking-[0.1em] text-center flex items-center justify-center hover:opacity-80 transition-opacity"
          >
            {t('brideAccount')}
          </button>
        </div>
      </div>
    );
  };

  const NoticeSectionContent = () => (
    <div className="flex flex-col items-center gap-4 w-full max-w-[328px] md:max-w-[500px] lg:max-w-[600px] mx-auto text-center">
      <h2 className="text-xl font-kopub font-bold text-wedding-gray-dark">
        {t('notice')}
      </h2>
      <p className="text-sm font-kopub text-wedding-gray-medium leading-[2em] tracking-[-0.03em] whitespace-pre-line">
        {t('notice1')}
      </p>

      {/* 장식 요소 */}
      {config.showNotice2 && (
        <div className="w-[12.69px] h-[12.69px] bg-wedding-green border-2 border-wedding-green-dark rotate-45"></div>
      )}

      {config.showNotice2 && (
        <p className="text-sm font-kopub text-wedding-gray-medium leading-[2em] tracking-[-0.03em] whitespace-pre-line">
          {t('notice2')}
        </p>
      )}
    </div>
  );

  return (
    <>
      {/* 본 페이지 */}
      <div className="w-full max-w-[480px] md:max-w-[768px] lg:max-w-[1024px] mx-auto bg-wedding-gray-bg relative">
        {/* 인트로 이미지 레이어 */}
        {showIntro && (
          <div className={`fixed inset-0 z-[9999] ${imageEnded ? 'bg-transparent animate-fadeOut' : 'bg-white'}`}>
            {/* 사진 공유 팝업 (결혼식 시작 시간 이후) */}
            {showPhotoSharePopup && (
              <div className="absolute inset-0 z-[10000] flex items-center justify-center bg-black/50">
                <div className="bg-white rounded-2xl mx-6 w-full max-w-[320px] overflow-hidden shadow-xl">
                  <div className="px-6 pt-7 pb-5 text-center">
                    <h3 className="text-lg font-pretendard font-bold text-[#333333] mb-3">참석해주셔서 감사합니다!</h3>
                    <p className="text-sm font-pretendard text-[#666666] leading-relaxed">공유해주시고 싶은 사진이 있으신 경우<br/>업로드 해주세요!</p>
                  </div>
                  <div className="flex border-t border-[#E5E5E5]">
                    <button
                      onClick={() => setShowPhotoSharePopup(false)}
                      className="flex-1 py-3.5 text-sm font-pretendard font-medium text-[#999999] border-r border-[#E5E5E5] active:bg-gray-50"
                    >
                      취소
                    </button>
                    <button
                      onClick={() => { window.location.href = 'https://k889fd6gxssqg1dujymmdqk9t9voqtx.direct.quickconnect.to:6601/mo/request/FZUxcKCvV'; }}
                      className="flex-1 py-3.5 text-sm font-pretendard font-semibold text-[#639B89] active:bg-gray-50"
                    >
                      공유
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* 로딩 화면 */}
            {isLoading && (
              <div className={`w-full h-full flex items-center justify-center bg-white absolute inset-0 ${imageLoaded ? 'animate-fadeOut' : ''}`}>
                {/* iOS 스타일 Circular Progress */}
                <div className="relative w-10 h-10">
                  <div className="absolute inset-0 border-2 border-gray-200 rounded-full"></div>
                  <div
                    className="absolute inset-0 border-2 border-transparent border-t-gray-400 rounded-full animate-spin"
                    style={{ animationDuration: '0.8s' }}
                  ></div>
                </div>
              </div>
            )}

            {/* 애니메이션 이미지 - 팝업이 떠있으면 로드하지 않음 */}
            <div className={`w-full h-full flex items-center justify-center overflow-hidden ${isLoading || showPhotoSharePopup ? 'opacity-0 absolute' : 'opacity-100'}`}>
              {!showPhotoSharePopup && (
                <img
                  ref={introImageRef}
                  key={imageTimestamp}
                  src={`${introImageUrl}?t=${imageTimestamp}`}
                  alt="인트로 애니메이션"
                  className="h-full w-auto object-cover"
                  onLoad={handleImageLoaded}
                  onError={handleImageError}
                />
              )}
            </div>
          </div>
        )}

        {/* 언어 변경 버튼 */}
        {config.showLanguageButton && (
          <div
            className="fixed top-4 right-4 z-50 transition-opacity duration-300"
            style={{ opacity: languageButtonOpacity, pointerEvents: languageButtonOpacity > 0 ? 'auto' : 'none' }}
          >
            <div className="relative">
              <button
                onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
                aria-label="language change"
                className="flex items-center gap-2 px-3 py-2 bg-white border border-[#DDDDDD] rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
              >
                <img src={languageIcon} alt="language" className="w-5 h-5" />
              </button>

              {/* 언어 선택 메뉴 */}
              {languageMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setLanguageMenuOpen(false)}
                  />
                  <div className="absolute top-full right-0 mt-2 bg-white border border-[#DDDDDD] rounded-lg shadow-lg z-50 min-w-[120px]">
                    {Object.keys(languageNames).map((lang) => (
                      <button
                        key={lang}
                        onClick={() => changeLanguage(lang)}
                        className={`w-full px-4 py-2 text-left text-sm font-pretendard font-medium transition-colors first:rounded-t-lg last:rounded-b-lg ${language === lang
                          ? 'bg-[#639B89] text-white'
                          : 'text-[#333333] hover:bg-gray-50'
                          }`}
                      >
                        {languageNames[lang]}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <Swiper
          direction="vertical"
          slidesPerView={1}
          mousewheel
          keyboard
          modules={[Mousewheel, Keyboard]}
          className="h-screen"
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            const index = swiper.activeIndex || 0;
            setActiveSection(index);
            prevActiveSectionRef.current = index;
          }}
          onSlideChange={(swiper) => {
            const currentIndex = swiper.activeIndex;
            const prevIndex = prevActiveSectionRef.current;

            setActiveSection(currentIndex);
            setLanguageMenuOpen(false);

            // 스와이프 방향에 따라 실제 스크롤 발생
            if (currentIndex !== prevIndex) {
              const scrollDirection = currentIndex > prevIndex ? 20 : -20;
              const currentScrollY = window.scrollY;

              // 실제 스크롤 발생 (브라우저가 감지할 수 있도록)
              window.scrollBy({ top: scrollDirection, behavior: 'auto' });

              // 약간의 지연 후 원래 위치로 복원 (브라우저가 스크롤을 감지한 후)
              setTimeout(() => {
                window.scrollTo({ top: currentScrollY, behavior: 'auto' });
              }, 50);
            }

            prevActiveSectionRef.current = currentIndex;
          }}
          onTransitionStart={(swiper) => {
            const currentIndex = swiper.activeIndex;
            const prevIndex = prevActiveSectionRef.current;

            // 스와이프 방향에 따라 실제 스크롤 발생
            if (currentIndex !== prevIndex) {
              const scrollDirection = currentIndex > prevIndex ? 20 : -20;
              const currentScrollY = window.scrollY;

              // 실제 스크롤 발생
              window.scrollBy({ top: scrollDirection, behavior: 'auto' });

              // 약간의 지연 후 원래 위치로 복원
              setTimeout(() => {
                window.scrollTo({ top: currentScrollY, behavior: 'auto' });
              }, 50);
            }
          }}
        >
          <SwiperSlide>
            {/* 헤더 섹션 */}
            <section className="h-screen flex px-4 md:px-8 lg:px-12 pt-6 md:pt-8 bg-white pb-[100px]">
              <div className="flex flex-col w-full max-w-[440px] md:max-w-[700px] lg:max-w-[960px] mx-auto h-full min-h-0">
                {/* 제목 */}
                <div className="pb-6 flex justify-center">
                  <h1 className="text-[28px] md:text-[32px] font-bodoni font-semibold text-wedding-gray-dark leading-[1.525] text-center px-2 whitespace-nowrap">
                    {t('weddingDay')}
                  </h1>
                </div>

                {/* 이미지: 남은 공간 우선 사용 */}
                <div className="flex-1 min-h-0 flex items-center justify-center">
                  <picture className="w-full h-full flex justify-center">
                    {headerImageWebp && <source srcSet={headerImageWebp} type="image/webp" />}
                    <img
                      src={headerImageSrc}
                      alt="웨딩 초대장"
                      className="h-full max-h-full w-auto max-w-full rounded-lg object-contain"
                    />
                  </picture>
                </div>

                {/* 신랑 신부 이름 및 날짜/장소 */}
                <div className="pt-8 flex flex-col items-center gap-6 w-full">
                  {/* 신랑 신부 이름 */}
                  <div className="flex items-center justify-center gap-4 w-full">
                    {language === 'ja' ? (
                      <>
                        <ruby className="text-2xl font-kopub text-wedding-gray-text tracking-[0.1em]">
                          賈<rt>カ</rt> 盛<rt>ソン</rt> 鉱<rt>ヒョン</rt>
                        </ruby>
                        <div className="w-[1px] h-[11.31px] bg-wedding-gray-light rotate-45"></div>
                        <ruby className="text-2xl font-kopub text-wedding-gray-text tracking-[0.1em]">
                          南<rt>ナン</rt>ウンキョル
                        </ruby>
                      </>
                    ) : (
                      <>
                        <span className="text-2xl font-kopub text-wedding-gray-text tracking-[0.1em]">가성현</span>
                        <div className="w-[1px] h-[11.31px] bg-wedding-gray-light rotate-45"></div>
                        <span className="text-2xl font-kopub text-wedding-gray-text tracking-[0.1em]">남은결</span>
                      </>
                    )}
                  </div>

                  {/* 날짜 및 장소 */}
                  <div className="flex flex-col items-center gap-2 w-full">
                    <p className="text-base font-kopub text-wedding-gray-medium text-center">
                      {t('date')}
                    </p>
                    <p className="text-base font-kopub text-wedding-gray-medium text-center">
                      {t('place')}
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </SwiperSlide>

          {/* 인용구 섹션 */}
          <SwiperSlide>
            <section className="min-h-screen flex items-center justify-center px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-white pb-[100px]">
              <div className="flex flex-col items-center gap-12 w-full max-w-[440px] md:max-w-[700px] lg:max-w-[960px] mx-auto">
                {/* 장식 요소 */}
                <picture>
                  {dividerWebp && <source srcSet={dividerWebp} type="image/webp" />}
                  <img src={dividerSrc} alt="divider" className="w-[168px] h-4 object-contain" />
                </picture>

                <div className="flex flex-col items-center gap-4 w-full">
                  <p className="text-base font-kopub text-wedding-gray-text leading-[1.5] text-center">
                    However big, however small,<br />
                    let me be part of it all.<br />
                    Share your dreams with me.
                  </p>
                  <p className="text-xs font-pretendard font-light text-wedding-gray-medium text-center">
                    {t('songCredit')}
                  </p>
                </div>

                {/* 장식 요소 */}
                <picture>
                  {dividerWebp && <source srcSet={dividerWebp} type="image/webp" />}
                  <img src={dividerSrc} alt="divider" className="w-[168px] h-4 object-contain" />
                </picture>
              </div>
            </section>
          </SwiperSlide>

          <SwiperSlide>
            {/* 부모님 소개 섹션 */}
            <section className="relative min-h-screen flex items-center px-4 md:px-8 lg:px-12 py-20 md:py-24 bg-white pb-[100px]">
              <div className="flex flex-col items-center gap-12 max-w-[440px] md:max-w-[700px] lg:max-w-[960px] mx-auto">
                {/* 장식 요소 */}
                <picture>
                  {dividerWebp && <source srcSet={dividerWebp} type="image/webp" />}
                  <img src={dividerSrc} alt="divider" className="w-[168px] h-4 object-contain" />
                </picture>

                {/* 인사말 */}
                <p
                  className="font-kopub font-light text-wedding-gray-text text-center leading-[2em] tracking-[0.01em] whitespace-pre-line"
                  style={{ fontSize: language === 'ja' ? '11px' : '16px' }}
                >
                  {t('greeting')}
                </p>

                {/* 부모님 정보 */}
                {language !== 'ja' && (
                  <div className="flex flex-col gap-4">
                    {/* 신랑 부모님 */}
                    <div className="flex items-center justify-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-xl font-kopub text-wedding-gray-medium">가재업 · 고복순</span>
                        <span className="text-xl font-kopub text-wedding-gray-medium w-10 text-right">{t('son')}</span>
                      </div>
                      <span className="text-xl font-kopub font-bold text-wedding-gray-dark tracking-[0.1em] text-right">성 현</span>
                    </div>

                    {/* 신부 부모님 */}
                    <div className="flex items-center justify-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-xl font-kopub text-wedding-gray-medium">남기환 · 박정연</span>
                        <span className="text-xl font-kopub text-wedding-gray-medium w-10 text-right">{t('daughter')}</span>
                      </div>
                      <span className="text-xl font-kopub font-bold text-wedding-gray-dark tracking-[0.1em] text-right">은 결</span>
                    </div>
                  </div>
                )}

                {/* 장식 요소 */}
                <picture>
                  {dividerWebp && <source srcSet={dividerWebp} type="image/webp" />}
                  <img src={dividerSrc} alt="divider" className="w-[168px] h-4 object-contain" />
                </picture>
              </div>
            </section>
          </SwiperSlide>

          <SwiperSlide>
            {/* 캘린더 섹션 */}
            <section className="relative min-h-screen flex items-center px-4 md:px-8 lg:px-12 py-6 md:py-8 bg-white pb-[100px]">
              <div className="flex flex-col gap-4 max-w-[440px] md:max-w-[700px] lg:max-w-[960px] mx-auto">
                {/* reception 모드일 때 피로연 안내 타이틀 */}
                {config.isReception && (
                  <h2 className="text-[32px] font-bodoni font-semibold text-wedding-gray-dark text-center leading-[1.525]">
                    {t('receptionInfo')}
                  </h2>
                )}

                {/* 날짜/장소 정보 */}
                <div className="flex flex-col items-center gap-2 py-2">
                  <p className="text-xl font-kopub font-bold text-wedding-gray-dark leading-[1.2em] text-center">
                    {t('dateShort')}
                  </p>
                  <p className="text-xl font-kopub font-bold text-wedding-gray-dark leading-[1.2em] text-center">
                    {t('place')}
                  </p>
                </div>

                {/* 구분선 */}
                <div className="w-full h-px bg-[#EEEEEE]"></div>

                {/* 캘린더 */}
                <div className="px-8 md:px-12 lg:px-16">
                  <div className="grid grid-cols-7">
                    {/* 날짜들 */}
                    {calendarDays.map((day) => {
                      // 3월 2026의 1일은 일요일
                      const dayOfWeek = (day - 1) % 7;
                      const isSunday = dayOfWeek === 0;
                      const isSelected = day === selectedDate;

                      return (
                        <div
                          key={day}
                          className={`flex items-center justify-center p-2 cursor-pointer ${isSelected
                            ? 'w-10 h-10 bg-wedding-green rounded-full text-white mx-auto'
                            : ''
                            }`}
                        >
                          <span className={`text-base font-kopub leading-[1.5em] ${isSelected
                            ? 'text-white'
                            : isSunday
                              ? 'text-[#FF5858]'
                              : 'text-[#111111]'
                            }`}>
                            {day}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* 카운트다운 */}
                <div className="flex flex-col items-center gap-4 pt-6">
                  <div className="flex gap-3 md:gap-4">
                    {/* Days */}
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-16 h-16 md:w-20 md:h-20 bg-[#F5F5F5] rounded-lg flex items-center justify-center">
                        <span className="text-2xl md:text-3xl font-kopub font-bold text-wedding-gray-dark">
                          {String(countdown.days).padStart(2, '0')}
                        </span>
                      </div>
                      <span className="text-xs md:text-sm font-kopub text-wedding-gray-medium">
                        {t('days')}
                      </span>
                    </div>

                    {/* Hours */}
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-16 h-16 md:w-20 md:h-20 bg-[#F5F5F5] rounded-lg flex items-center justify-center">
                        <span className="text-2xl md:text-3xl font-kopub font-bold text-wedding-gray-dark">
                          {String(countdown.hours).padStart(2, '0')}
                        </span>
                      </div>
                      <span className="text-xs md:text-sm font-kopub text-wedding-gray-medium">
                        {t('hours')}
                      </span>
                    </div>

                    {/* Minutes */}
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-16 h-16 md:w-20 md:h-20 bg-[#F5F5F5] rounded-lg flex items-center justify-center">
                        <span className="text-2xl md:text-3xl font-kopub font-bold text-wedding-gray-dark">
                          {String(countdown.minutes).padStart(2, '0')}
                        </span>
                      </div>
                      <span className="text-xs md:text-sm font-kopub text-wedding-gray-medium">
                        {t('minutes')}
                      </span>
                    </div>

                    {/* Seconds */}
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-16 h-16 md:w-20 md:h-20 bg-[#F5F5F5] rounded-lg flex items-center justify-center">
                        <span className="text-2xl md:text-3xl font-kopub font-bold text-wedding-gray-dark">
                          {String(countdown.seconds).padStart(2, '0')}
                        </span>
                      </div>
                      <span className="text-xs md:text-sm font-kopub text-wedding-gray-medium">
                        {t('seconds')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </SwiperSlide>

          <SwiperSlide>
            {/* 갤러리 섹션 */}
            <section className="relative min-h-screen flex items-center px-4 md:px-8 lg:px-12 py-8 md:py-12 bg-white pb-[100px]">
              <div className="flex flex-col items-center gap-4 max-w-[440px] md:max-w-[700px] lg:max-w-[960px] mx-auto">
                {/* GALLERY 제목과 큰 이미지 */}
                <div className="flex flex-col gap-4 w-full">
                  <h2 className="text-[32px] font-bodoni font-semibold text-wedding-gray-dark text-center leading-[1.525]">
                    {t('gallery')}
                  </h2>
                  {/* 큰 이미지 */}
                  <picture className="w-full flex justify-center">
                    {gallery1Webp && <source srcSet={gallery1Webp} type="image/webp" />}
                    <img
                      src={gallery1Src}
                      alt="갤러리 메인"
                      className="max-h-[60vh] w-auto max-w-full md:max-w-[700px] lg:max-w-[960px] mx-auto cursor-pointer object-contain"
                      onClick={() => handleImageClick(0)}
                    />
                  </picture>
                </div>
                {/* 작은 이미지들 */}
                <div className="flex gap-2 overflow-x-auto w-full scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                  {galleryImages.slice(1).map((img, i) => (
                    <picture key={i} className="flex-shrink-0">
                      {img.webp && <source srcSet={img.webp} type="image/webp" />}
                      <img
                        src={img.fallback}
                        alt={`갤러리 ${i + 2}`}
                        className="w-[88px] h-[88px] aspect-square object-cover cursor-pointer"
                        onClick={() => handleImageClick(i + 1)}
                      />
                    </picture>
                  ))}
                </div>
              </div>
            </section>
          </SwiperSlide>

          {/* 이미지 뷰어 모달 */}
          {viewerOpen && (
            <div
              className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center"
              onClick={() => setViewerOpen(false)}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* 닫기 버튼 */}
              <button
                className="absolute top-4 md:top-8 right-4 md:right-8 text-white text-2xl md:text-3xl font-bold w-10 h-10 md:w-12 md:h-12 flex items-center justify-center hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setViewerOpen(false);
                }}
              >
                ×
              </button>

              {/* 이전 버튼 */}
              <button
                className="absolute left-4 md:left-8 text-white text-2xl md:text-3xl font-bold w-12 h-12 md:w-16 md:h-16 flex items-center justify-center hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevious();
                }}
              >
                ‹
              </button>

              {/* 이미지 */}
              <div
                className="max-w-[90vw] md:max-w-[85vw] lg:max-w-[80vw] max-h-[90vh] flex items-center justify-center px-4"
                onClick={(e) => e.stopPropagation()}
              >
                <picture>
                  {galleryImages[currentImageIndex].webp && (
                    <source srcSet={galleryImages[currentImageIndex].webp} type="image/webp" />
                  )}
                  <img
                    src={galleryImages[currentImageIndex].fallback}
                    alt={`갤러리 ${currentImageIndex + 1}`}
                    className="max-w-full max-h-[90vh] object-contain"
                  />
                </picture>
              </div>

              {/* 다음 버튼 */}
              <button
                className="absolute right-4 md:right-8 text-white text-2xl md:text-3xl font-bold w-12 h-12 md:w-16 md:h-16 flex items-center justify-center hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNext();
                }}
              >
                ›
              </button>

              {/* 이미지 인덱스 표시 */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black bg-opacity-50 px-4 py-2 rounded-full">
                {currentImageIndex + 1} / {galleryImages.length}
              </div>
            </div>
          )}

          {/* 섹션 구분선 */}
          <div className="h-2 bg-[#F5F5F5]"></div>

          {/* 메시지 섹션 */}
          <SwiperSlide>
            <section className="h-screen flex px-4 py-8 bg-white pb-[100px]">
              <div className="flex flex-col w-full max-w-[440px] md:max-w-[700px] lg:max-w-[960px] mx-auto h-full min-h-0">
                {/* 상단 타이틀 */}
                <div className="pt-2 pb-4 flex justify-center">
                  <h2 className="text-[32px] font-bodoni font-semibold text-[#333333] leading-[1.525] text-center">
                    {t('message')}
                  </h2>
                </div>

                {/* 메시지 영역 */}
                <div
                  ref={messageListRef}
                  className="flex-1 min-h-0 flex flex-col items-center justify-center gap-4 w-full px-2"
                >
                  {loadingMessages ? (
                    <div className="text-center py-8 text-[#999999] text-base font-pretendard font-light w-full max-w-[640px] self-center">
                      {t('loadingMessages')}
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-8 text-[#999999] text-base font-pretendard font-light w-full max-w-[640px] self-center">
                      {t('noMessages')}
                    </div>
                  ) : (
                    paginatedMessages.map((msg, idx) => (
                      <div
                        key={msg.id || `${currentMessagePage}-${idx}`}
                        className="flex gap-2 py-3 border-b border-[#EEEEEE] w-full max-w-[720px] self-center cursor-pointer"
                        onClick={() => handleOpenViewMessage(msg)}
                      >
                        <div className="flex flex-col gap-2 flex-1 py-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-base font-pretendard font-medium text-[#111111]">
                              {msg.name}
                            </span>
                            <span className="text-base font-pretendard font-light text-[#999999]">
                              {msg.time || msg.createdAt}
                            </span>
                          </div>
                          <p className="text-base font-pretendard font-light text-[#666666] whitespace-nowrap overflow-hidden text-ellipsis">
                            {msg.text || msg.message}
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDeleteModal(msg.id);
                          }}
                          className="w-6 h-6 bg-[#EEEEEE] rounded-lg flex-shrink-0 flex items-center justify-center p-1 hover:bg-[#DDDDDD] transition-colors cursor-pointer"
                        >
                          <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.05128 12.6666L3.33333 11.9487L7.28205 7.99998L3.33333 4.05126L4.05128 3.33331L8 7.28203L11.9487 3.33331L12.6667 4.05126L8.71794 7.99998L12.6667 11.9487L11.9487 12.6666L8 8.71793L4.05128 12.6666Z" fill="#999999" />
                          </svg>
                        </button>
                      </div>
                    ))
                  )}
                </div>

                {/* 하단 페이지/버튼 */}
                <div className="pt-6 flex flex-col gap-4">
                  <div className="flex items-center justify-between w-full">
                    {/* 왼쪽 화살표 */}
                    <button
                      onClick={handlePrevMessagePage}
                      disabled={currentMessagePage === 1 || loadingMessages || messages.length === 0}
                      className={`w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full border border-transparent transition-colors ${currentMessagePage === 1 || loadingMessages || messages.length === 0 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-[#F5F5F5]'}`}
                      aria-label="previous messages page"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.84 5.84L8.46 12L15.84 18.16" stroke="#999999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>

                    {/* 페이지 번호 */}
                    <div className="flex items-center gap-1">
                      {messages.length === 0 ? (
                        <div className="text-sm text-[#999999] px-2">{t('noMessages')}</div>
                      ) : (
                        messagePageNumbers.map((num) => (
                          <button
                            key={num}
                            onClick={() => setCurrentMessagePage(num)}
                            className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors ${currentMessagePage === num ? 'bg-[#639B89] text-white' : 'text-[#999999] hover:bg-[#F5F5F5]'}`}
                            aria-label={`page ${num}`}
                          >
                            <span className="text-base font-pretendard font-light">
                              {num}
                            </span>
                          </button>
                        ))
                      )}
                    </div>

                    {/* 오른쪽 화살표 */}
                    <button
                      onClick={handleNextMessagePage}
                      disabled={currentMessagePage === totalMessagePages || loadingMessages || messages.length === 0}
                      className={`w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full border border-transparent transition-colors ${currentMessagePage === totalMessagePages || loadingMessages || messages.length === 0 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-[#F5F5F5]'}`}
                      aria-label="next messages page"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.16 5.84L15.54 12L8.16 18.16" stroke="#999999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>

                  <button
                    onClick={() => setMessageModalOpen(true)}
                    className="w-full py-4 px-2 bg-[#639B89] rounded-[50px] text-white text-lg font-pretendard font-medium"
                  >
                    {t('leaveMessage')}
                  </button>
                </div>
              </div>
            </section>
          </SwiperSlide>

          {/* 메시지 전체 보기 모달 */}
          {viewMessageModalOpen && viewMessage && (
            <>
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-40 animate-fadeIn"
                onClick={handleCloseViewMessage}
              />
              <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
                <div className="bg-white rounded-2xl shadow-xl max-w-[520px] w-full max-h-[80vh] overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-[#EEEEEE]">
                    <div className="flex flex-col">
                      <span className="text-base font-pretendard font-semibold text-[#111111]">
                        {viewMessage.name}
                      </span>
                      <span className="text-sm font-pretendard font-light text-[#999999]">
                        {viewMessage.time || viewMessage.createdAt}
                      </span>
                    </div>
                    <button
                      onClick={handleCloseViewMessage}
                      className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F5F5] transition-colors"
                      aria-label="close message detail"
                    >
                      ×
                    </button>
                  </div>
                  <div className="px-5 py-4 overflow-auto" style={{ maxHeight: 'calc(80vh - 64px)' }}>
                    <p className="text-base font-pretendard font-light text-[#333333] whitespace-pre-wrap break-words leading-6">
                      {viewMessage.text || viewMessage.message}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* 메시지 작성 바텀시트 모달 */}
          {messageModalOpen && (
            <>
              {/* 배경 오버레이 */}
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-40 animate-fadeIn"
                onClick={() => setMessageModalOpen(false)}
              />

              {/* 바텀시트 */}
              <div className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-[32px] px-4 pt-8 pb-4 max-w-[480px] md:max-w-[768px] lg:max-w-[1024px] mx-auto animate-slideUp">
                <div className="flex flex-col items-center gap-8 w-full">
                  {/* 제목 영역 */}
                  <div className="flex flex-col items-center gap-4">
                    <h3 className="text-xl font-kopub font-bold text-[#333333] text-right">
                      {t('messageTitle')}
                    </h3>
                    <p className="text-base font-kopub font-normal text-[#666666] text-right">
                      {t('messageDesc')}
                    </p>
                  </div>

                  {/* 입력 폼 */}
                  <div className="flex flex-col gap-4 w-full">
                    {/* 이름과 비밀번호 입력 */}
                    <div className="flex flex-col gap-2 w-full">
                      <div className="flex gap-2">
                        {/* 이름 입력 */}
                        <div className="flex-1">
                          <input
                            type="text"
                            placeholder={t('name')}
                            value={messageForm.name}
                            maxLength={15}
                            onChange={(e) => setMessageForm({ ...messageForm, name: e.target.value.slice(0, 15) })}
                            className="w-full h-10 px-4 py-2 border border-[#DDDDDD] rounded-lg text-base font-pretendard font-medium text-[#111111] placeholder:text-[#999999] focus:outline-none focus:border-[#639B89]"
                          />
                        </div>

                        {/* 비밀번호 입력 */}
                        <div className="flex-1">
                          <input
                            type="password"
                            placeholder={t('password')}
                            value={messageForm.password}
                            onChange={(e) => setMessageForm({ ...messageForm, password: e.target.value })}
                            className="w-full h-10 px-4 py-2 border border-[#DDDDDD] rounded-lg text-base font-pretendard font-medium text-[#111111] placeholder:text-[#999999] focus:outline-none focus:border-[#639B89]"
                          />
                        </div>
                      </div>
                    </div>

                    {/* 메시지 입력 */}
                    <div className="w-full">
                      <textarea
                        placeholder={t('messagePlaceholder')}
                        value={messageForm.message}
                        maxLength={140}
                        onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value.slice(0, 140) })}
                        rows={6}
                        className="w-full h-40 px-4 py-2 border border-[#DDDDDD] rounded-lg text-base font-pretendard font-medium text-[#111111] placeholder:text-[#999999] focus:outline-none focus:border-[#639B89] resize-none"
                      />
                    </div>

                    {/* 메시지 남기기 버튼 */}
                    <button
                      onClick={handleSubmitMessage}
                      disabled={submittingMessage}
                      className="w-full py-4 px-2 bg-[#639B89] rounded-2xl text-white text-lg font-pretendard font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submittingMessage ? t('submitting') : t('submitMessage')}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* 메시지 삭제 바텀시트 모달 */}
          {deleteModalOpen && (
            <>
              {/* 배경 오버레이 */}
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-40 animate-fadeIn"
                onClick={() => {
                  setDeleteModalOpen(false);
                  setSelectedMessageId(null);
                  setDeletePassword('');
                }}
              />

              {/* 바텀시트 */}
              <div className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-[32px] px-4 pt-8 pb-4 max-w-[480px] md:max-w-[768px] lg:max-w-[1024px] mx-auto animate-slideUp">
                <div className="flex flex-col items-center gap-8 w-full">
                  {/* 제목 영역 */}
                  <div className="flex flex-col items-center gap-4">
                    <h3 className="text-xl font-kopub font-bold text-[#333333] text-right">
                      {t('deleteMessage')}
                    </h3>
                    <p className="text-base font-kopub font-normal text-[#666666] text-right">
                      {t('deletePasswordDesc')}
                    </p>
                  </div>

                  {/* 비밀번호 입력 */}
                  <div className="flex flex-col gap-4 w-full">
                    <input
                      type="password"
                      placeholder={t('password')}
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleDeleteMessage();
                        }
                      }}
                      className="w-full h-10 px-4 py-2 border border-[#DDDDDD] rounded-lg text-base font-pretendard font-medium text-[#111111] placeholder:text-[#999999] focus:outline-none focus:border-[#639B89]"
                    />

                    {/* 버튼들 */}
                    <div className="flex gap-2 w-full">
                      <button
                        onClick={() => {
                          setDeleteModalOpen(false);
                          setSelectedMessageId(null);
                          setDeletePassword('');
                        }}
                        className="flex-1 py-4 px-2 bg-[#EEEEEE] rounded-2xl text-[#666666] text-lg font-pretendard font-medium"
                      >
                        {t('cancel')}
                      </button>
                      <button
                        onClick={handleDeleteMessage}
                        disabled={deletingMessage || !deletePassword.trim()}
                        className="flex-1 py-4 px-2 bg-[#FF5858] rounded-2xl text-white text-lg font-pretendard font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {deletingMessage ? t('deleting') : t('delete')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* 섹션 구분선 */}
          <div className="h-2 bg-[#F5F5F5]"></div>

          {/* 오시는 길 섹션 */}
          <SwiperSlide>
            <section className="h-screen flex flex-col px-4 md:px-8 lg:px-12 py-8 md:py-12 bg-white pb-[100px]">
              <div className="flex flex-col items-center gap-4 w-full h-full min-h-0">
                {/* 제목과 주소 */}
                <div className="flex flex-col items-center gap-4">
                  <h2 className="text-xl font-kopub font-bold text-wedding-gray-dark text-center">
                    {t('directions')}
                  </h2>
                  <p className="text-base font-kopub text-wedding-gray-medium text-center">
                    {t('address')}
                  </p>
                </div>

                {/* 지도 영역, 버튼, 교통 정보 */}
                <div className="flex-1 min-h-0 flex flex-col gap-4 w-full">
                  <div className="flex-1 min-h-[100px] w-full">
                    <div
                      ref={mapRef}
                      id="map"
                      className="w-full h-full min-h-[100px] rounded-[8px] overflow-hidden"
                      style={{ minHeight: '100px' }}
                    />
                  </div>

                  {/* 지도 앱 버튼 */}
                  <div className="flex gap-2 w-full">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleNaverMap();
                      }}
                      className="flex-1 flex flex-wrap items-center justify-center gap-2 py-4 px-4 bg-[#5FA854] rounded-[8px] text-white text-base font-pretendard font-medium"
                    >
                      <img src={naverMapIcon} alt="네이버 지도" className="w-5 h-5 rounded object-cover flex-shrink-0" />
                      <span className="text-center">{t('naver')}</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleKakaoMap();
                      }}
                      className="flex-1 flex flex-wrap items-center justify-center gap-2 py-4 px-4 bg-[#F4E235] rounded-[8px] text-[#303030] text-base font-pretendard font-medium"
                    >
                      <img src={kakaoMapIcon} alt="카카오 지도" className="w-5 h-5 rounded border border-[#F5F5F5] object-cover flex-shrink-0" />
                      <span className="text-center">{t('kakao')}</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleGoogleMap();
                      }}
                      className="flex-1 flex flex-wrap items-center justify-center gap-2 py-4 px-4 bg-white border border-[#DDDDDD] rounded-[8px] text-wedding-gray-dark text-base font-pretendard font-medium"
                    >
                      <img src={googleMapIcon} alt="구글 지도" className="w-5 h-5 rounded border border-[#F5F5F5] object-cover flex-shrink-0" />
                      <span className="text-center">{t('google')}</span>
                    </button>
                  </div>

                  {/* 교통 정보 */}
                  {config.showTransportInfo && (
                    <div className="flex flex-col gap-2 w-full">
                      {/* 대중 교통 */}
                      <div className="flex flex-col gap-2 py-2 px-0 border-b border-[#EEEEEE]">
                        <div className="flex flex-col gap-2 py-1">
                          <h3 className="text-base font-kopub font-bold text-wedding-gray-dark text-left">
                            {t('publicTransport')}
                          </h3>
                          <p className="text-base font-pretendard font-light text-wedding-gray-medium leading-[1.5em] text-left whitespace-pre-line">
                            {t('publicTransportDesc')}
                          </p>
                        </div>
                      </div>

                      {/* 자가용 */}
                      {language !== 'ja' && (
                        <div className="flex flex-col gap-2 py-2 px-0">
                          <div className="flex flex-col gap-2 py-1">
                            <h3 className="text-base font-kopub font-bold text-wedding-gray-dark text-left">
                              {t('car')}
                            </h3>
                            <p className="text-base font-pretendard font-light text-wedding-gray-medium leading-[1.5em] text-left whitespace-pre-line">
                              {t('carDesc')}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </section>
          </SwiperSlide>

          {/* 섹션 구분선 */}
          <div className="h-2 bg-[#F5F5F5]"></div>

          {/* 마음 전하는 곳 + 안내사항 섹션 */}
          {config.showGiftSection ? (
            splitGiftAndNotice ? (
              <>
                {language === 'ko' && (
                  <SwiperSlide>
                    <section className="min-h-screen flex items-center justify-center px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-white pb-[100px]">
                      <GiftSectionContent />
                    </section>
                  </SwiperSlide>
                )}

                {!config.isReception && (
                  <>
                    <div className="h-2 bg-[#F5F5F5]"></div>
                    <SwiperSlide>
                      <section className="min-h-screen flex items-center justify-center px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-white pb-[100px]">
                        <NoticeSectionContent />
                      </section>
                    </SwiperSlide>
                  </>
                )}
              </>
            ) : (
              <SwiperSlide>
                <section className="min-h-screen flex items-center justify-center px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-white pb-[100px]">
                  <div className="flex flex-col items-center gap-12 w-full max-w-[720px]">
                    {language === 'ko' && (
                      <>
                        <GiftSectionContent />
                        {!config.isReception && <div className="w-full h-px bg-[#F5F5F5]"></div>}
                      </>
                    )}
                    {!config.isReception && <NoticeSectionContent />}
                  </div>
                </section>
              </SwiperSlide>
            )
          ) : (
            !config.isReception && (
              <SwiperSlide>
                <section className="min-h-screen flex items-center justify-center px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-white pb-[100px]">
                  <NoticeSectionContent />
                </section>
              </SwiperSlide>
            )
          )}

        </Swiper>

        {/* 높이 측정용 숨김 섹션 */}
        <div
          ref={giftNoticeMeasureRef}
          aria-hidden
          className="absolute w-full left-0"
          style={{ visibility: 'hidden', pointerEvents: 'none', top: 0 }}
        >
          <div className="px-4 md:px-8 lg:px-12 py-12 md:py-16">
            <div className="flex flex-col items-center gap-12 w-full max-w-[720px] mx-auto">
              {config.showGiftSection && language === 'ko' && (
                <>
                  <GiftSectionContent />
                  {!config.isReception && <div className="w-full h-px bg-[#F5F5F5]"></div>}
                </>
              )}
              {!config.isReception && <NoticeSectionContent />}
            </div>
          </div>
        </div>

      </div>
    </>
  );
}

export default App;
